import React, { PropsWithChildren, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { Token } from '@components/Tokens/TokenSelector/tokenSchemas'
import { TokenSelection } from '@components/Tokens/TokenSelector/TokenSelection'
import { TokenType } from '@components/Tokens/types'
import { isDefined } from '@towns-protocol/sdk'
import { Icon, IconButton, Stack, Text } from '@ui'
import { useBalance } from 'hooks/useBalance'
import { Address } from 'use-towns-client'
import { zeroAddress } from 'viem'

import { SingleSelectionSelector } from './SingleSelectionSelector'
import { TransferSchema } from './transferAssetsSchema'

export const ETH_OPTION = {
    chainId: 0o0,
    data: {
        address: zeroAddress,
        type: TokenType.UNKNOWN,
        label: 'BASE ETH',
        tokenId: '0',
    },
}

export function AssetSelector(props: {
    fromWallet: Address | undefined
    fromBalance: ReturnType<typeof useBalance>['data']
    nfts: Token[]
}) {
    const { fromBalance, nfts } = props
    const { watch, setValue } = useFormContext<TransferSchema>()
    const assetAddressOrIdentifier = watch('assetAddressOrIdentifier')

    const options: Token[] = useMemo(() => {
        return [ETH_OPTION, ...nfts].filter((asset) => isDefined(asset.data.address))
    }, [nfts])

    const onAddItem = (token: Token) => {
        const isEth = token.data.address === ETH_OPTION.data.address
        setValue('assetAddressOrIdentifier', isEth ? 'BASE_ETH' : (token.data.address as Address))
        setValue('tokenId', isEth ? undefined : token.data.tokenId)
    }

    const singleSelection = (removeCb: () => void) => {
        const onRemoveClick = () => {
            setValue('assetAddressOrIdentifier', undefined)
            removeCb()
        }
        if (assetAddressOrIdentifier === 'BASE_ETH') {
            return (
                <AssetToTransfer onRemoveClick={onRemoveClick}>
                    <EthDetail fromBalance={fromBalance} />
                </AssetToTransfer>
            )
        } else if (assetAddressOrIdentifier) {
            const asset = options.find((t) => t.data.address === assetAddressOrIdentifier)
            if (!asset) {
                return <Text color="error">Asset not found</Text>
            }
            return (
                <AssetToTransfer onRemoveClick={onRemoveClick}>
                    <TokenSelection
                        token={asset}
                        optionBoxProps={{ paddingY: 'none', paddingX: 'none' }}
                    />
                </AssetToTransfer>
            )
        }
    }

    return (
        <Stack gap data-testid="asset-search">
            <SingleSelectionSelector
                options={options}
                label="Search Assets"
                placeholder="Search Assets"
                keys={['data.address', 'data.tokenId', 'data.name', 'data.label']}
                optionRenderer={(args) => (
                    <AssetOption token={args.option} onAddItem={() => onAddItem(args.option)} />
                )}
                getOptionKey={(opt) => opt?.data?.address + opt?.data?.tokenId}
                singleSelection={(onRemoveClick) => singleSelection(onRemoveClick)}
            />
        </Stack>
    )
}

export function AssetToTransfer(
    props: PropsWithChildren & {
        onRemoveClick: () => void
    },
) {
    return (
        <Stack
            horizontal
            padding
            rounded="sm"
            background="level2"
            justifyContent="spaceBetween"
            alignItems="center"
            data-testid="asset-to-transfer"
        >
            {props.children}
            <IconButton
                data-testid="remove-asset"
                icon="close"
                tooltip="Unselect"
                onClick={props.onRemoveClick}
            />
        </Stack>
    )
}

function AssetOption(props: { token: Token; onAddItem: (customKey?: string) => void }) {
    const { token, onAddItem } = props

    const onClick = () => {
        onAddItem(token.data.address)
    }

    if (token.data.address === ETH_OPTION.data.address) {
        return <EthOption onAddItem={onClick} />
    }

    return (
        <Stack
            cursor="pointer"
            data-testid={`asset-option-${token.data.address}`}
            onClick={() => onAddItem(token.data.address)}
        >
            <TokenSelection token={token} optionBoxProps={{ paddingY: 'sm', hoverable: true }} />
        </Stack>
    )
}

function EthOption(props: { onAddItem: () => void; address?: Address }) {
    const { onAddItem } = props

    return (
        <Stack
            hoverable
            cursor="pointer"
            padding="sm"
            rounded="sm"
            background="level2"
            data-testid="asset-option-eth"
            onClick={onAddItem}
        >
            <EthDetail />
        </Stack>
    )
}

export function EthDetail(props: { fromBalance?: ReturnType<typeof useBalance>['data'] }) {
    const { fromBalance } = props
    return (
        <Stack horizontal alignItems="center" gap="sm">
            <Icon type="base" size="square_x5" color="coinbaseBlue" />
            <Stack gap="sm">
                <Text>{fromBalance ? fromBalance.formatted : ''} ETH</Text>
                <Text size="sm" color="gray2">
                    Base
                </Text>
            </Stack>
        </Stack>
    )
}
